<template>
  <b-modal
    v-model="isOpen"
    id="place-selection-modal"
    size="xxl"
    no-close-on-backdrop
    no-close-on-esc
    hide-footer
    body-class="p-0"
    @shown="onModalShown"
    @hidden="onModalHidden"
  >
    <template #modal-header>
      <h5 class="modal-title mb-0">Add Places</h5>
      <div class="d-flex align-items-center ml-auto">
        <b-button
          v-if="selectedPlaces.length > 0"
          ref="selectedPlacesBtn"
          variant="outline-primary"
          size="sm"
          class="mr-3 selected-places-btn"
          :class="{ 'count-pulse': countPulse }"
          @click="requestShowReview"
        >
          Selected Places ({{ selectedPlaces.length }})
        </b-button>
        <b-button variant="link" class="text-muted p-0" @click="cancelSelection">
          &#x2715;
        </b-button>
      </div>
    </template>

    <!-- Transient "+N" badge that animates toward the "Selected Places (N)"
         header button, reinforcing where just-added places went. Purely
         decorative (aria-hidden); the toast conveys the same change to AT. -->
    <div
      v-if="flightActive"
      class="added-flight"
      :style="flightStyle"
      aria-hidden="true"
      @animationend="flightActive = false"
    >
      +{{ flightCount }}
    </div>

    <div class="modal-body-inner place-selection-two-column">

      <div class="place-selection-left">
        <PlaceSelectionSharedMap
          v-if="modalReady"
          :mode="sharedMapMode"
          :explore-map-data="exploreMapData"
          :explore-zoom-command="exploreZoomCommand"
          :upload-preview-features="uploadPreviewFeatures"
          :draw-command="drawCommand"
          :review-edit-place-id="reviewEditingPlaceId"
          :review-edit-command="reviewEditCommand"
          :review-focus-command="reviewFocusCommand"
          @explore-point-clicked="onExplorePointClicked"
          @viewport-changed="onExploreViewportChanged"
          @draw-state-changed="onDrawStateChanged"
          @review-edit-saved="onReviewEditGeometrySaved"
          @geocoded-country="onGeocodedCountry"
        />
      </div>

      <div class="place-selection-right">
        <!-- Review step -->
        <PlaceSelectionReview
          v-if="showReview"
          v-show="modalReady"
          :editing-place-id="reviewEditingPlaceId"
          :is-saving="isSavingCustom"
          :can-save-custom-places="hasNpaasAccess"
          :is-applying-library-tags="isApplyingLibraryTags"
          :library-tag-error="libraryTagError"
          :library-tag-reset-token="libraryTagResetToken"
          @back="onReviewBack"
          @edit-start="onReviewEditStart"
          @edit-save="onReviewEditSave"
          @edit-cancel="onReviewEditCancel"
          @place-click="onReviewPlaceClick"
          @save-custom-places="onSaveCustomPlaces"
          @apply-library-tags="onApplyLibraryTags"
        />

        <!-- Tab step -->
        <b-tabs
          v-else
          v-show="modalReady"
          v-model="activeTabIndex"
          @activate-tab="onActivateTab"
          content-class="pt-0"
          no-fade
          nav-class="px-3 pt-2"
          fill
        >
          <b-tab title="Explore">
            <PlaceSelectionSelectTab
              v-if="modalReady && activeTabIndex === 0"
              :map-click-event="exploreMapClickEvent"
              :geocoded-country-command="geocodedCountryCommand"
              :viewport-command="viewportCommand"
              @explore-data-changed="onExploreDataChanged"
              @explore-zoom-to="onExploreZoomTo"
              @place-limit-hit="onPlaceLimitHit"
            />
          </b-tab>
          <b-tab title="Draw">
            <PlaceSelectionDrawTab
              v-if="modalReady && activeTabIndex === 1"
              :drawn-shapes="drawMapState.drawnShapes"
              :is-satellite-view="drawMapState.isSatelliteView"
              :geocoded-address="drawMapState.geocodedAddress"
              :geocoded-name="drawMapState.geocodedName"
              @draw-command="onDrawCommand"
            />
          </b-tab>
          <b-tab title="Upload">
            <PlaceSelectionUploadTab
              v-if="modalReady && activeTabIndex === 2"
              @preview-features-changed="onUploadPreviewFeaturesChanged"
            />
          </b-tab>
        </b-tabs>
      </div>      
    </div>
    <div class="modal-footer-bar px-3 py-2 border-top d-flex justify-content-between align-items-center">
      <b-button variant="outline-secondary" @click="cancelSelection">Cancel</b-button>
      <div class="d-flex align-items-center">
        <b-alert
          v-if="confirmError"
          show
          variant="danger"
          class="mb-0 mr-3 py-1 px-2"
        >
          {{ confirmError }}
        </b-alert>
        <b-alert
          v-if="footerLimitAlert"
          show
          variant="warning"
          class="mb-0 mr-3 py-1 px-2"
        >
          {{ footerLimitAlert }}
        </b-alert>
        <b-button
          v-if="showReview"
          class="mr-3"
          variant="outline-primary"
          :disabled="!!reviewEditingPlaceId"
          @click="showReview = false"
        >
          Add More Places
        </b-button>
        <transition name="added-confirm-fade">
          <span v-if="addedConfirmMessage" class="added-confirm mr-3">
            <b-icon icon="check-circle-fill" class="mr-1" />{{ addedConfirmMessage }}
          </span>
        </transition>
        <span v-if="selectedPlaces.length > 0" class="text-muted mr-3">
          {{ selectedPlaces.length }}<template v-if="hasPlaceLimit"> / {{ maxPlaces }}</template> place(s) selected
        </span>
        <b-button
          v-if="!showReview"
          variant="primary"
          :disabled="selectedPlaces.length === 0 || overPlaceLimit"
          @click="requestShowReview"
        >
          Review
        </b-button>
        <b-button
          v-if="showReview"
          variant="primary"
          :disabled="selectedPlaces.length === 0 || isSavingCustom || !!reviewEditingPlaceId || overPlaceLimit"
          @click="confirmSelection"
        >
          Continue with Selected
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { GetMixin } from 'pinnacle-lib';
import PlacesService from '@/services/dataExplorer/placesService.js';
import PlaceSelectionSelectTab from './placeSelection/PlaceSelectionSelectTab.vue';
import PlaceSelectionDrawTab from './placeSelection/PlaceSelectionDrawTab.vue';
import PlaceSelectionUploadTab from './placeSelection/PlaceSelectionUploadTab.vue';
import PlaceSelectionReview from './placeSelection/PlaceSelectionReview.vue';
import PlaceSelectionSharedMap from './placeSelection/PlaceSelectionSharedMap.vue';
import { buildLibraryBody } from './placeSelection/placeSelectionCsv';
import { partitionPlaces, partitionLibraryPlaces, extractSavedPoiId } from './placeSelection/placeSelectionSaveHelpers';
import { computeAddedDelta, formatAddedConfirmation } from './placeSelection/placeSelectionAddedFeedback';
import { deriveGeometry } from './placeSelection/builderGeoJson';
import { reverseGeocodeCountry } from '@/services/dataExplorer/reverseGeocodeService';
import {
  POLL_INTERVAL_MS,
  MAX_POLL_ATTEMPTS,
  POI_PLACES_TYPE,
  selectEntryById,
  isTerminalStatus,
  formatStatusMessage,
  formatTimeoutMessage,
  formatStatusErrorMessage,
  shouldScheduleNext,
  buildInsertBatchId,
  filterDisplayablePois,
  extractJobId,
} from './placeSelection/csvJobStatusHelpers';

export default {
  name: 'PlaceSelectionModal',
  mixins: [GetMixin, PlacesService],
  components: {
    PlaceSelectionSelectTab,
    PlaceSelectionDrawTab,
    PlaceSelectionUploadTab,
    PlaceSelectionReview,
    PlaceSelectionSharedMap,
  },
  data() {
    return {
      showReview: false,
      isFinalizing: false,
      isSavingCustom: false,
      confirmError: '',
      isApplyingLibraryTags: false,
      libraryTagError: '',
      libraryTagResetToken: '',
      modalReady: false,
      // Transient inline notice shown in the footer when an add was truncated
      // by the per-flow Max_Places cap. Rendered as a b-alert (not a toast)
      // because the modal's z-index sits above the toaster. Auto-clears.
      placeLimitNotice: '',
      // "Places added" feedback state (toast + counter pulse + flight badge).
      // prevSelectedCount tracks the last-seen selected count so the watcher can
      // detect a genuine increase; pulse/flight are transient animation flags.
      prevSelectedCount: 0,
      countPulse: false,
      flightActive: false,
      flightCount: 0,
      flightStyle: null,
      // Transient inline footer confirmation (e.g. "2 places added") shown
      // next to the selected-count; auto-clears after a few seconds.
      addedConfirmMessage: '',
      uploadPreviewFeatures: [],
      exploreMapData: null,
      exploreZoomCommand: null,
      exploreMapClickEvent: null,
      // Token-bearing map viewport command relayed to the Select tab so it can
      // auto-fill polygon geometries for in-view places when zoomed in.
      viewportCommand: null,
      drawCommand: null,
      geocodedCountryCommand: null,
      reviewEditCommand: null,
      reviewFocusCommand: null,
      reviewEditingPlaceId: null,
      pendingReviewEditPayload: null,
      // Post-registration CSV job poller state (csv-upload-status-and-poi-display).
      csvJob: {
        jobId: null,
        entryId: null, // _id of the CSV place being polled (stays in "Places to Save")
        attempts: 0,
        cancelled: false,
        timer: null, // setTimeout handle for the next Poll_Cycle
        statusMessage: '', // Status_Message text (Requirement 4)
        state: 'idle', // 'idle' | 'polling' | 'completed' | 'failed' | 'timeout' | 'error'
        isFetchingPois: false,
      },
      // Whether the most recent Poll_Cycle errored — drives the status-unavailable
      // vs timeout message choice in scheduleNextOrTimeout (Requirement 8.2).
      csvJobLastCycleErrored: false,
      drawMapState: {
        drawnShapes: [],
        isSatelliteView: false,
        geocodedAddress: '',
        geocodedName: '',
        isDrawing: false,
        canUndo: false,
        canRedo: false,
        canDelete: false,
      },
    };
  },
  computed: {
    placeSelectionState() {
      return this.$sessionStore.state.placeSelection;
    },
    isOpen: {
      get() {
        return this.placeSelectionState.isOpen;
      },
      set(value) {
        if (value) return;
        if (!this.placeSelectionState.isOpen) return;
        this.cancelSelection();
      },
    },
    selectedPlaces() {
      return this.placeSelectionState.selectedPlaces;
    },
    // Per-flow selection cap threaded in via openPlaceSelectionModal. 0 means
    // "no limit" (the default), in which case the modal imposes no restriction.
    maxPlaces() {
      const cap = Number(this.placeSelectionState.maxPlaces);
      return Number.isFinite(cap) && cap > 0 ? cap : 0;
    },
    // Whether a finite cap is in effect for this flow.
    hasPlaceLimit() {
      return this.maxPlaces > 0;
    },
    // True once the selection has reached the cap (used to stop further adds).
    atPlaceLimit() {
      return this.hasPlaceLimit && this.selectedPlaces.length >= this.maxPlaces;
    },
    // True when the selection has exceeded the cap. This gates Review/Continue
    // so the user must remove places before proceeding, rather than silently
    // discarding extras after the modal closes.
    overPlaceLimit() {
      return this.hasPlaceLimit && this.selectedPlaces.length > this.maxPlaces;
    },
    // Inline message shown next to the footer actions when the cap is exceeded.
    placeLimitMessage() {
      if (!this.overPlaceLimit) return '';
      return this.maxPlaces === 1
        ? 'This flow allows a maximum of 1 place. Remove places to continue.'
        : `This flow allows a maximum of ${this.maxPlaces} places. Remove places to continue.`;
    },
    // The single limit message rendered in the footer. The persistent
    // over-limit message (which blocks Review/Continue) takes precedence over
    // the transient "add was truncated" notice.
    footerLimitAlert() {
      return this.placeLimitMessage || this.placeLimitNotice;
    },
    // Dedicated reactive count for the "places added" watcher. Reading .length
    // off the array inside a computed establishes a clean reactive dependency
    // that fires reliably on the in-place pushes the store mutations perform
    // (a string-path watch on 'selectedPlaces.length' did not fire).
    selectedCount() {
      const list = this.placeSelectionState.selectedPlaces;
      return Array.isArray(list) ? list.length : 0;
    },
    activeTabIndex: {
      get() {
        const tabMap = { select: 0, draw: 1, upload: 2 };
        return tabMap[this.placeSelectionState.activeTab] || 0;
      },
      set(index) {
        const tabs = ['select', 'draw', 'upload'];
        const nextTab = tabs[index] || 'select';
        this.$sessionStore.commit('setActiveTab', nextTab);
      },
    },
    sharedMapMode() {
      if (this.showReview) {
        return this.reviewEditingPlaceId ? 'review-edit' : 'review';
      }
      return this.placeSelectionState.activeTab || 'select';
    },
    hasNpaasAccess() {
      const user = this.$persistingStore.state.user;
      const companyId = user.selected_company_id || user.defaultCompanyId || null;
      const hasCompanies = Array.isArray(user.companies) && user.companies.length > 0;
      return !!companyId && hasCompanies;
    },
    hasCustomPlacesSelected() {
      return this.selectedPlaces.some(
        (p) => p && ((p.geometry && ['draw', 'upload'].includes(p._source)) || p._source === 'csv'),
      );
    },
  },
  watch: {
    isOpen(val) {
      if (!val) {
        // Tear down the CSV job poller and reset its state (Requirement 3.4).
        this.cancelCsvJobPolling();
        this.csvJob = {
          jobId: null,
          entryId: null,
          attempts: 0,
          cancelled: false,
          timer: null,
          statusMessage: '',
          state: 'idle',
          isFetchingPois: false,
        };
        this.csvJobLastCycleErrored = false;
        this.showReview = false;
        this.isFinalizing = false;
        this.isSavingCustom = false;
        this.confirmError = '';
        this.clearPlaceLimitNoticeTimer();
        this.placeLimitNotice = '';
        this.modalReady = false;
        // Reset "places added" feedback state and clear any pending pulse timer
        // so a reopen starts clean and no animation lingers after close.
        this.clearCountPulseTimer();
        this.clearAddedConfirmTimer();
        this.prevSelectedCount = 0;
        this.countPulse = false;
        this.flightActive = false;
        this.flightCount = 0;
        this.addedConfirmMessage = '';
        this.uploadPreviewFeatures = [];
        this.exploreMapData = null;
        this.exploreZoomCommand = null;
        this.exploreMapClickEvent = null;
        this.drawCommand = null;
        this.geocodedCountryCommand = null;
        this.reviewEditCommand = null;
        this.reviewFocusCommand = null;
        this.reviewEditingPlaceId = null;
        this.pendingReviewEditPayload = null;
        this.drawMapState = {
          drawnShapes: [],
          isSatelliteView: false,
          geocodedAddress: '',
          geocodedName: '',
          isDrawing: false,
          canUndo: false,
          canRedo: false,
          canDelete: false,
        };
      }
    },
    sharedMapMode(mode) {
      if (mode !== 'upload') {
        this.uploadPreviewFeatures = [];
      }
      if (mode !== 'select') {
        this.exploreZoomCommand = null;
      }
    },
    // Central "places added" seam: every add path (Draw, Upload, Explore, and
    // the async CSV poller) funnels through selectedPlaces in the store, so a
    // single watch on its length detects an increase from any tab and drives
    // all feedback. Decreases (removals) and the reset-to-0 on close are
    // ignored by computeAddedDelta. prevSelectedCount is seeded on modal open
    // so pre-existing selections don't fire feedback on open.
    selectedCount(next) {
      const added = computeAddedDelta(this.prevSelectedCount, next);
      this.prevSelectedCount = Number.isFinite(next) ? next : 0;
      if (added > 0) {
        this.onPlacesAdded(added, this.prevSelectedCount);
      }
    },
  },
  methods: {
    onActivateTab(newTabIndex, prevTabIndex, bvEvent) {
      const tabs = ['select', 'draw', 'upload'];
      const fromTab = tabs[prevTabIndex] || (this.placeSelectionState.activeTab || 'select');
      const toTab = tabs[newTabIndex] || fromTab;

      if (fromTab === toTab) return;

      if (!this.confirmPendingDiscardForTab(fromTab, 'switch tabs')) {
        if (bvEvent && typeof bvEvent.preventDefault === 'function') {
          bvEvent.preventDefault();
        }
      }
    },
    getPendingUnaddedCountForTab(tab) {
      if (tab === 'select') {
        const selected = this.exploreMapData && Array.isArray(this.exploreMapData.selectedPlacematrixIds)
          ? this.exploreMapData.selectedPlacematrixIds
          : [];
        return selected.length;
      }

      if (tab === 'draw') {
        return Array.isArray(this.drawMapState.drawnShapes) ? this.drawMapState.drawnShapes.length : 0;
      }

      if (tab === 'upload') {
        return Array.isArray(this.uploadPreviewFeatures) ? this.uploadPreviewFeatures.length : 0;
      }

      return 0;
    },
    getPendingDiscardMessage(tab, destinationLabel) {
      if (tab === 'select') {
        return `You have selected place(s) in Explore that are not added yet. Continue to ${destinationLabel} and discard them?`;
      }

      if (tab === 'draw') {
        return `You have drawn shape(s) that are not added yet. Continue to ${destinationLabel} and discard them?`;
      }

      if (tab === 'upload') {
        return `You have uploaded/selected feature(s) that are not added yet. Continue to ${destinationLabel} and discard them?`;
      }

      return `You have unsaved changes. Continue to ${destinationLabel}?`;
    },
    confirmPendingDiscardForTab(tab, destinationLabel) {
      const pendingCount = this.getPendingUnaddedCountForTab(tab);
      if (pendingCount <= 0) return true;

      const msg = this.getPendingDiscardMessage(tab, destinationLabel);
      return window.confirm(msg);
    },
    // Show a transient inline footer notice when the Select tab reports that an
    // add was truncated by the per-flow cap. Inline (not a toast) so it is
    // visible above the modal. Restart the auto-clear timer on repeated hits.
    onPlaceLimitHit(message) {
      if (!message) return;
      this.placeLimitNotice = message;
      this.clearPlaceLimitNoticeTimer();
      this._placeLimitNoticeTimer = setTimeout(() => {
        this.placeLimitNotice = '';
        this._placeLimitNoticeTimer = null;
      }, 5000);
    },
    clearPlaceLimitNoticeTimer() {
      if (this._placeLimitNoticeTimer) {
        clearTimeout(this._placeLimitNoticeTimer);
        this._placeLimitNoticeTimer = null;
      }
    },
    requestShowReview() {
      if (this.showReview) return;

      // Block entering Review while over the per-flow cap. The footer Review
      // button is already disabled, but the header "Selected Places (N)" button
      // also calls this, so guard here too. The footer already shows the
      // persistent over-limit alert (placeLimitMessage), so no toast is fired —
      // the modal's z-index sits above the toaster and would hide it anyway.
      if (this.overPlaceLimit) {
        return;
      }

      const currentTab = this.placeSelectionState.activeTab || 'select';
      if (!this.confirmPendingDiscardForTab(currentTab, 'Review')) {
        return;
      }

      this.showReview = true;
    },
    confirmDiscardActiveEdit() {
      if (!this.reviewEditingPlaceId) return true;
      return window.confirm('You have unsaved place edits. Discard changes and continue?');
    },
    onReviewBack() {
      if (!this.confirmDiscardActiveEdit()) return;

      if (this.reviewEditingPlaceId) {
        this.pendingReviewEditPayload = null;
        this.reviewEditCommand = {
          action: 'cancel',
          payload: { id: this.reviewEditingPlaceId },
          token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        };
        this.reviewEditingPlaceId = null;
      }

      this.showReview = false;
    },
    onExploreDataChanged(payload = null) {
      this.exploreMapData = payload;
    },
    onExploreZoomTo(payload = null) {
      if (!payload || !Array.isArray(payload.center)) return;
      this.exploreZoomCommand = {
        center: payload.center,
        zoom: payload.zoom,
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onExplorePointClicked(placematrixId) {
      if (!placematrixId) return;
      this.exploreMapClickEvent = {
        placematrixId,
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onExploreViewportChanged(payload = null) {
      if (!payload || !payload.bounds || typeof payload.zoom !== 'number') return;
      // Token-bearing command so the Select tab's deep watcher fires even when
      // two consecutive viewports are numerically identical.
      this.viewportCommand = {
        bounds: payload.bounds,
        zoom: payload.zoom,
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onGeocodedCountry(alpha3) {
      if (!alpha3) return;
      // Token-bearing command so the Select_Tab's deep watcher fires even for
      // two identical consecutive codes.
      this.geocodedCountryCommand = {
        alpha3,
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onUploadPreviewFeaturesChanged(features = []) {
      console.log('Received upload preview features:', features);
      this.uploadPreviewFeatures = Array.isArray(features) ? features : [];
    },
    onDrawCommand(command = null) {
      if (!command || !command.action) return;
      this.drawCommand = {
        action: command.action,
        payload: command.payload || null,
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onDrawStateChanged(state = {}) {
      this.drawMapState = {
        drawnShapes: Array.isArray(state.drawnShapes) ? state.drawnShapes : [],
        isSatelliteView: !!state.isSatelliteView,
        geocodedAddress: state.geocodedAddress || '',
        geocodedName: state.geocodedName || '',
        isDrawing: !!state.isDrawing,
        canUndo: !!state.canUndo,
        canRedo: !!state.canRedo,
        canDelete: !!state.canDelete,
      };
    },
    onReviewPlaceClick({ id } = {}) {
      if (!id) return;
      // Token-bearing command so the map's deep watcher fires even when the
      // same place is clicked twice in a row.
      this.reviewFocusCommand = {
        id,
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onReviewEditStart({ id } = {}) {
      if (!id) return;
      this.confirmError = '';
      this.pendingReviewEditPayload = null;
      this.reviewEditingPlaceId = id;
      this.reviewEditCommand = {
        action: 'start',
        payload: { id },
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onReviewEditSave(payload = {}) {
      if (!payload || !payload.id || payload.id !== this.reviewEditingPlaceId) return;

      this.pendingReviewEditPayload = {
        id: payload.id,
        name: payload.name,
        address: payload.address,
        category: payload.category,
        brand: payload.brand,
      };

      this.reviewEditCommand = {
        action: 'save',
        payload: { id: payload.id },
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
    },
    onReviewEditCancel({ id } = {}) {
      if (!id || id !== this.reviewEditingPlaceId) return;

      this.pendingReviewEditPayload = null;
      this.reviewEditCommand = {
        action: 'cancel',
        payload: { id },
        token: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
      this.reviewEditingPlaceId = null;
    },
    onReviewEditGeometrySaved(payload = {}) {
      const pending = this.pendingReviewEditPayload;
      if (!pending || !pending.id || payload.id !== pending.id) return;

      this.$sessionStore.commit('updateSelectedPlaceFields', pending);
      if (payload.geometry) {
        this.$sessionStore.commit('updateSelectedPlaceGeometry', {
          id: pending.id,
          geometry: payload.geometry,
        });
      }

      this.pendingReviewEditPayload = null;
      this.reviewEditingPlaceId = null;
    },
    // Whether the user has requested reduced motion. When true we keep the
    // toast (which conveys the change non-visually) but skip the pulse/flight
    // animations for accessibility.
    prefersReducedMotion() {
      return (
        typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    },
    // Central "places added" handler invoked by the selectedPlaces.length
    // watcher on any genuine increase. Shows a toast (always) plus the counter
    // pulse and flight badge (motion permitting).
    onPlacesAdded(addedCount) {
      this.showAddedConfirmation(addedCount);

      if (this.prefersReducedMotion()) return;

      this.triggerCountPulse();
      this.triggerFlight(addedCount);
    },
    // Show a brief inline confirmation in the footer next to the selected
    // count, auto-clearing after a few seconds. Restart the timer cleanly on
    // rapid, repeated adds so the message reflects the latest add.
    showAddedConfirmation(addedCount) {
      this.clearAddedConfirmTimer();
      this.addedConfirmMessage = formatAddedConfirmation(addedCount);
      this._addedConfirmTimer = setTimeout(() => {
        this.addedConfirmMessage = '';
        this._addedConfirmTimer = null;
      }, 3000);
    },
    clearAddedConfirmTimer() {
      if (this._addedConfirmTimer) {
        clearTimeout(this._addedConfirmTimer);
        this._addedConfirmTimer = null;
      }
    },
    clearCountPulseTimer() {
      if (this._countPulseTimer) {
        clearTimeout(this._countPulseTimer);
        this._countPulseTimer = null;
      }
    },
    // Pulse the header "Selected Places (N)" button. Toggle the class off first
    // (across a tick) so the CSS animation replays on rapid, repeated adds.
    triggerCountPulse() {
      this.clearCountPulseTimer();
      this.countPulse = false;
      this.$nextTick(() => {
        this.countPulse = true;
        this._countPulseTimer = setTimeout(() => {
          this.countPulse = false;
          this._countPulseTimer = null;
        }, 600);
      });
    },
    // Show the transient "+N" badge flying toward the header button. Anchor the
    // flight end point to the button's live position when available so it lands
    // on the counter; fall back to a fixed top-right anchor otherwise. Restart
    // cleanly if a previous flight is still running.
    triggerFlight(addedCount) {
      this.flightActive = false;
      this.flightCount = addedCount;
      this.$nextTick(() => {
        this.flightStyle = this.computeFlightStyle();
        this.flightActive = true;
      });
    },
    computeFlightStyle() {
      const btn = this.$refs.selectedPlacesBtn;
      const el = btn && btn.$el ? btn.$el : btn;
      if (el && typeof el.getBoundingClientRect === 'function') {
        const rect = el.getBoundingClientRect();
        return {
          '--flight-end-x': `${Math.round(rect.left + rect.width / 2)}px`,
          '--flight-end-y': `${Math.round(rect.top + rect.height / 2)}px`,
        };
      }
      return null;
    },
    onModalShown() {
      this.modalReady = true;
      this.confirmError = '';
      this.isFinalizing = false;
      this.showReview = !!this.placeSelectionState.initialShowReview;
      // Seed the added-feedback baseline so any places already selected when the
      // modal opens do not trigger a spurious "added" toast/animation.
      this.prevSelectedCount = this.selectedPlaces.length;
    },
    onModalHidden() {
      // Stop any in-flight CSV job polling when the modal is hidden (Requirement 3.4).
      this.cancelCsvJobPolling();
      if (this.placeSelectionState.isOpen) {
        this.cancelSelection();
      }
      this.modalReady = false;
      this.isFinalizing = false;
    },
    getCompanyId() {
      return this.$persistingStore.state.user.selected_company_id || this.$persistingStore.state.user.defaultCompanyId || null;
    },
    // Best-effort detect the alpha-3 country for a place from its center
    // coordinate via reverse geocoding. Never throws and returns null on any
    // failure (bad/absent center, network error, no match); the server then
    // falls back to the tenant country, so the save proceeds regardless.
    async detectCountryForPlace(place) {
      const hasCenter = Array.isArray(place.centerLngLat) && place.centerLngLat.length === 2;
      if (!hasCenter) return null;
      const [lon, lat] = place.centerLngLat;
      return reverseGeocodeCountry(lon, lat);
    },
    // `country` is the detected alpha-3 (or null). It is included at the top
    // level of the payload only when present; when null it is omitted so the
    // server applies its tenant-country fallback.
    buildCustomPlaceSavePayload(place, country = null) {
      const companyId = this.getCompanyId();
      if (!companyId) {
        throw new Error('Unable to determine company id for place save.');
      }

      const hasCenter = Array.isArray(place.centerLngLat) && place.centerLngLat.length === 2;
      const lon = hasCenter ? place.centerLngLat[0] : null;
      const lat = hasCenter ? place.centerLngLat[1] : null;
      const isUpload = (place._source || 'library') === 'upload';

      const payload = {
        properties: {
          name: place.name,
          address: place.address || null,
          cat2: place.category || (isUpload ? 'Uploaded' : 'Drawn'),
          poi_list_name: place.brand || place.fileName || 'PlaceSelection',
          lat,
          lon,
          data_source: 'Places',
          tags: [isUpload ? 'Upload' : 'Draw'],
          labels: place.labels || [],
        },
        geometry: place.geometry,
        companyId,
      };

      // Only attach a detected country; a null lets the server fall back to the
      // tenant's country (and finally USA).
      if (country) {
        payload.country = country;
      }

      return payload;
    },
    async saveCustomPlace(place) {
      // Detect the country from the place center before building the payload.
      // Best-effort: a null detection is fine (server falls back to tenant).
      const country = await this.detectCountryForPlace(place);
      const payload = this.buildCustomPlaceSavePayload(place, country);
      // Return the response body so the caller can capture the created
      // near_poi_id and set it on the local place (makes the freshly saved
      // Custom place taggable in the Review flow).
      return this.postToURL('/v2/save-custom-place', payload);
    },
    async persistCustomPlacesOnConfirm() {
      const customPlaces = this.selectedPlaces.filter((p) => p && p.geometry && ['draw', 'upload'].includes(p._source));
      if (!customPlaces.length) return { failed: [], poiIdsById: {} };

      const results = await Promise.allSettled(customPlaces.map((place) => this.saveCustomPlace(place)));

      const failed = [];
      const poiIdsById = {};
      results.forEach((result, index) => {
        const place = customPlaces[index];
        if (result.status === 'rejected') {
          failed.push(place);
          return;
        }
        // Capture the created near_poi_id when the response carries one.
        const poiId = extractSavedPoiId(result.value);
        if (poiId) {
          poiIdsById[place._id] = poiId;
        } else {
          // Save succeeded but we couldn't locate the created id in the
          // response. The place still flips to the Custom library, but it
          // won't be taggable until the library is re-fetched. Surface this so
          // the response shape can be reconciled with extractSavedPoiId.
          console.warn('save-custom-place succeeded but no near_poi_id found in response', result.value);
        }
      });

      return { failed, poiIdsById };
    },
    async registerCsvEntriesOnConfirm() {
      const csvEntries = this.selectedPlaces.filter((p) => p && p._source === 'csv' && p.uploadPath);
      if (!csvEntries.length) return { failed: [], poiIdsById: {}, jobIdsById: {} };

      const companyId = this.getCompanyId();
      const results = await Promise.allSettled(
        csvEntries.map((entry) => this.postToURL('/v2/places/library', buildLibraryBody(entry, companyId))),
      );

      const failed = [];
      const poiIdsById = {};
      // Separate from poiIdsById: the CSV registration Job_Id is the library
      // entry `id` at `payload.id` (extractJobId), NOT a near_poi_id. It must
      // never be merged into poiIdsById, which markSelectedPlaceSaved consumes
      // as a near_poi_id map (Requirement 2.9).
      const jobIdsById = {};
      results.forEach((result, index) => {
        const entry = csvEntries[index];
        if (result.status === 'rejected') {
          failed.push(entry);
          return;
        }
        // A library registration does not return a near_poi_id, so poiIdsById
        // is left unset here; extractSavedPoiId is retained only in case a
        // future response shape carries one.
        const poiId = extractSavedPoiId(result.value);
        if (poiId) poiIdsById[entry._id] = poiId;
        // Capture the Job_Id for status polling (Requirement 2.9).
        const jobId = extractJobId(result.value);
        if (jobId) jobIdsById[entry._id] = jobId;
      });

      return { failed, poiIdsById, jobIdsById };
    },
    // --- CSV job poller (csv-upload-status-and-poi-display) -----------------
    // Push the current poller Status_Message onto the polled CSV place so the
    // Review "Places to Save" row shows a live processing indicator. `processing`
    // controls whether the spinner shows; pass false to clear it on a terminal
    // outcome while leaving the row in place.
    syncCsvPlaceProcessing(processing) {
      if (!this.csvJob.entryId) return;
      this.$sessionStore.commit('setCsvPlaceProcessing', {
        id: this.csvJob.entryId,
        processing,
        message: this.csvJob.statusMessage,
      });
    },
    // Begin polling the Library_Status_Proxy for a freshly registered CSV job.
    // The CSV entry stays in "Places to Save" (not flipped to the library) with
    // a processing indicator until a terminal poll outcome. Requirements 2.1, 4.5.
    startCsvJobPolling(jobId, entryId = null) {
      if (!jobId) return; // guard on a non-empty Job_Id
      this.csvJob.jobId = jobId;
      this.csvJob.entryId = entryId;
      this.csvJob.attempts = 0;
      this.csvJob.cancelled = false;
      this.csvJob.state = 'polling';
      this.csvJobLastCycleErrored = false;
      // Clear any prior Status_Message before the new job (Requirement 4.5) and
      // seed the processing message.
      this.csvJob.statusMessage = formatStatusMessage('processing');
      this.syncCsvPlaceProcessing(true);
      this.runPollCycle();
    },
    // One Poll_Cycle: GET the status proxy, decide continue / stop.
    // Requirements 2.2-2.8, 3.1-3.3, 3.5, 8.1, 8.2.
    async runPollCycle() {
      if (this.csvJob.cancelled) return;
      this.csvJob.attempts += 1;
      this.csvJobLastCycleErrored = false;
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken(); // Requirement 2.8
        const headers = this.getHeaders();
        const response = await this.$http({
          method: 'GET',
          params: { companyId: this.getCompanyId() }, // Requirement 2.2 (no tenant field)
          ...headers,
          url: `${process.env.VUE_APP_PINNACLE_API}/v2/places-library-status`,
        });
        // Late responses after cancel must not mutate state.
        if (this.csvJob.cancelled) return;

        const entries = response && response.data; // bare Library_Entry[] (envelope stripped)
        const entry = selectEntryById(entries, this.csvJob.jobId); // Requirement 2.3
        if (entry && isTerminalStatus(entry.status)) {
          // Terminal: stop and render the final Status_Message (Requirements 2.5, 2.6).
          this.csvJob.statusMessage = formatStatusMessage(entry.status, entry.stats);
          this.csvJob.state = entry.status === 'completed' ? 'completed' : 'failed';
          if (entry.status === 'completed') {
            this.fetchCsvJobPois(this.csvJob.jobId); // Requirement 2.5, 6.1
          } else {
            // Failure terminal: stop the processing spinner but keep the CSV
            // entry in "Places to Save" with the failure message so the user
            // can see the outcome and retry the save.
            this.finalizeCsvPlace(false);
          }
          return; // stop polling on any terminal status
        }
        // Still processing OR job absent from the response (Requirement 2.7).
        this.csvJob.statusMessage = formatStatusMessage('processing');
        this.syncCsvPlaceProcessing(true);
      } catch (error) {
        // Requirement 3.5 / 8.1: this cycle is counted; keep going.
        console.error('CSV job status poll cycle failed', error);
        this.csvJobLastCycleErrored = true;
      }
      this.scheduleNextOrTimeout();
    },
    // Schedule the next Poll_Cycle after Poll_Interval, or stop at the attempt
    // cap with the appropriate final message. Requirements 3.2, 3.3, 8.2.
    scheduleNextOrTimeout() {
      if (this.csvJob.cancelled) return;
      if (!shouldScheduleNext(this.csvJob.attempts)) {
        // attempts >= MAX_POLL_ATTEMPTS -> stop.
        if (this.csvJobLastCycleErrored) {
          this.csvJob.statusMessage = formatStatusErrorMessage('status'); // Requirement 8.2
          this.csvJob.state = 'error';
        } else {
          this.csvJob.statusMessage = formatTimeoutMessage(); // Requirement 3.3
          this.csvJob.state = 'timeout';
        }
        // Stopped without completion: clear the processing indicator but keep
        // the (final) message on the row in "Places to Save".
        this.finalizeCsvPlace();
        return;
      }
      this.csvJob.timer = setTimeout(() => this.runPollCycle(), POLL_INTERVAL_MS); // Requirement 3.1
    },
    // Flip the polled CSV entry out of "Places to Save" into the library once
    // processing has finished (completed with POIs fetched, failed, or timed
    // out), and clear its processing indicator. When `flipToLibrary` is false
    // the row is left in "Places to Save" (used for terminal states that never
    // produced library POIs) with the final Status_Message still shown.
    finalizeCsvPlace(flipToLibrary = false) {
      const entryId = this.csvJob.entryId;
      if (!entryId) return;
      if (flipToLibrary) {
        this.$sessionStore.commit('markSelectedPlaceSaved', { ids: [entryId] });
      } else {
        // Keep the row in place; just stop the spinner and retain the message.
        this.$sessionStore.commit('setCsvPlaceProcessing', {
          id: entryId,
          processing: false,
          message: this.csvJob.statusMessage,
        });
      }
    },
    // Cancel the poller and clear the pending timer (Requirement 3.4). Touches
    // no Custom places, so any already-displayed POIs persist.
    cancelCsvJobPolling() {
      this.csvJob.cancelled = true;
      clearTimeout(this.csvJob.timer);
      this.csvJob.timer = null;
    },
    // Fetch the processed POIs for a completed CSV job and add the displayable
    // ones to the placeSelection session store so they render under Custom.
    // Exactly one POI request is sent per completed job (invoked only from the
    // terminal 'completed' branch of runPollCycle). Requirements 6.1-6.7, 8.3, 8.4.
    async fetchCsvJobPois(jobId) {
      this.csvJob.isFetchingPois = true;
      try {
        if (this.tokenNeedsRefresh()) await this.refreshToken(); // Requirement 6.4
        const body = {
          companyId: this.getCompanyId(), // Requirement 6.3
          placesType: POI_PLACES_TYPE, // Requirement 6.2 (["Polygons","POI"])
          insert_batch_id: [buildInsertBatchId(jobId)], // Requirement 6.2 (["aui:"+jobId])
        };
        const res = await this.$http.post(
          `${process.env.VUE_APP_PINNACLE_API}/v2/places-search-pois`,
          body,
          this.getHeaders()
        );
        const records = (res && res.data && res.data.records) || []; // Requirement 6.5
        const places = filterDisplayablePois(records); // Requirement 6.6, 7.6
        // Non-empty -> add to the store (dedupe by _id). Empty -> add nothing and
        // keep the completion message (Requirement 6.6).
        if (places.length) {
          this.$sessionStore.commit('addSelectedPlaces', places); // Requirements 7.1-7.5
        }
        // Processing finished successfully: flip the source CSV entry out of
        // "Places to Save" now that its processed POIs (if any) are in the
        // Custom group, and stop its processing indicator.
        this.finalizeCsvPlace(true);
      } catch (error) {
        // Requirements 6.7 / 8.3: surface a fetch error, add zero POIs. Already
        // added Custom places remain untouched (Requirement 8.4).
        console.error('CSV job POI fetch failed', error);
        this.csvJob.statusMessage = formatStatusErrorMessage('pois');
        this.csvJob.state = 'error';
        // Keep the CSV entry in "Places to Save" with the error message; just
        // stop the spinner so the user can retry the save.
        this.finalizeCsvPlace(false);
      } finally {
        this.csvJob.isFetchingPois = false;
      }
    },
    async onSaveCustomPlaces() {
      if (this.isSavingCustom) return;
      if (this.reviewEditingPlaceId) {
        this.confirmError = 'Please save or cancel place edits before saving.';
        return;
      }
      if (!this.hasNpaasAccess) return;

      this.confirmError = '';
      this.isSavingCustom = true;

      try {
        const custom = await this.persistCustomPlacesOnConfirm();
        const csv = await this.registerCsvEntriesOnConfirm();
        const failed = [...custom.failed, ...csv.failed];
        const failedIds = new Set(failed.map((p) => p._id));
        // Merge the created near_poi_id maps from both save paths so each
        // succeeded place can be flipped to a taggable Custom library place.
        const poiIdsById = { ...custom.poiIdsById, ...csv.poiIdsById };

        const attempted = this.selectedPlaces.filter(
          (p) => (p.geometry && ['draw', 'upload'].includes(p._source)) ||
            (p._source === 'csv' && p.uploadPath),
        );
        // CSV entries that registered successfully enter the polling lifecycle:
        // they must STAY in "Places to Save" with a processing indicator, so
        // they are excluded from the immediate library flip here. The poller
        // (finalizeCsvPlace) flips them to the library once processing finishes.
        const pollingCsvIds = new Set(Object.keys(csv.jobIdsById));
        const succeededIds = attempted
          .map((p) => p._id)
          .filter((id) => !failedIds.has(id) && !pollingCsvIds.has(id));

        if (succeededIds.length) {
          this.$sessionStore.commit('markSelectedPlaceSaved', { ids: succeededIds, poiIdsById });
        }

        if (failed.length) {
          this.$sessionStore.commit('setSelectedPlaceSaveErrors', {
            failedIds: [...failedIds],
            message: 'Save failed. Retry save.',
          });

          const failedNames = failed.map((p) => p.name).filter(Boolean);
          this.confirmError = failedNames.length
            ? `Unable to save ${failedNames.length} place(s): ${failedNames.join(', ')}. Please retry.`
            : `Unable to save ${failed.length} place(s). Please retry.`;
        } else {
          this.$sessionStore.commit('setSelectedPlaceSaveErrors', { failedIds: [] });
        }

        // Begin polling the freshly registered CSV job (Requirements 2.1, 2.9).
        // A successful CSV registration records its Job_Id in `csv.jobIdsById`
        // (entry._id -> Job_Id from payload.id) and starts in the `'processing'`
        // status; that entry was not in `csv.failed`. Read from jobIdsById, NOT
        // poiIdsById (which carries near_poi_id values). Scope to the single-entry
        // case matching the current one-CSV-per-selection UX (the poller helpers
        // are list-agnostic and generalize to multiple jobs later).
        const csvEntryId = Object.keys(csv.jobIdsById)[0];
        const csvJobId = csvEntryId ? csv.jobIdsById[csvEntryId] : undefined;
        if (csvJobId) {
          this.startCsvJobPolling(csvJobId, csvEntryId);
        }
      } catch (error) {
        console.error('Explicit custom place save failed', error);
        this.confirmError = 'Unable to save selected places. Please retry.';
      } finally {
        this.isSavingCustom = false;
      }
    },
    async onApplyLibraryTags({ tags } = {}) {
      // R13.1/R13.2: block while a place edit is open; surface an inline message.
      if (this.reviewEditingPlaceId) {
        this.libraryTagError = 'Please finish editing the place before applying tags.';
        this.confirmError = 'Please finish editing the place before applying tags.';
        return;
      }

      // R13.3: block re-entrant applies while one is in flight.
      if (this.isApplyingLibraryTags) return;

      // R11.1: collect usable poi_ids from the current library places. Tags can
      // only be applied to Custom places (Azira catalog places are not
      // taggable), so restrict to the Custom sub-group.
      const library = partitionPlaces(this.selectedPlaces).library;
      const customLibrary = partitionLibraryPlaces(library).custom;
      // Only Custom places that carry a resolvable near_poi_id can be tagged.
      const taggable = customLibrary.filter((p) => p.near_poi_id);
      const poiIds = taggable.map((p) => p.near_poi_id).filter(Boolean);
      // Local ids of the Custom places we tag, so the post-apply merge only
      // touches those rows (not Azira library places).
      const customPlaceIds = taggable.map((p) => p._id).filter(Boolean);

      // R11.2: nothing to tag -> inline message, no HTTP, no state change.
      if (poiIds.length === 0) {
        // Distinguish "no Custom places at all" from "Custom places exist but
        // none have a resolvable id yet". The latter happens for places just
        // saved in this session, because the Places API polygon-create response
        // does not return the new near_poi_id; those become taggable once the
        // library is re-fetched (e.g. reopening the selection).
        this.libraryTagError = customLibrary.length > 0
          ? 'Newly saved places can\'t be tagged until they finish syncing to the library. Reopen the place selection to tag them.'
          : 'No custom places available to tag.';
        return;
      }

      const appliedTags = Array.isArray(tags) ? tags : [];

      // Apply immediately when Apply is clicked (no confirmation step).
      this.libraryTagError = '';
      this.isApplyingLibraryTags = true;
      const companyId = this.getCompanyId();
      const country = 'USA';

      try {
        const res = await this.addLabelsToPlaces({ companyId, poiIds, labels: appliedTags, country });
        if (res && res.success !== false) {
          // R12.1: merge tags onto the tagged Custom places and reset the panel.
          this.$sessionStore.commit('addLabelsToLibraryPlaces', { ids: customPlaceIds, labels: appliedTags });
          this.libraryTagResetToken = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        } else {
          // R12.2: surface a descriptive failure message.
          this.libraryTagError = (res && res.error && res.error.message) || 'Unable to apply tags. Please retry.';
        }
      } catch (error) {
        // R12.2: thrown errors surface the same way.
        console.error('Library bulk tag apply failed', error);
        this.libraryTagError = 'Unable to apply tags. Please retry.';
      } finally {
        this.isApplyingLibraryTags = false;
      }
    },
    confirmSelection() {
      if (this.reviewEditingPlaceId) {
        this.confirmError = 'Please save or cancel place edits before confirming.';
        return;
      }

      // Hard stop if the selection exceeds the per-flow cap. Keeps the resolved
      // result within the limit so the caller never has to silently truncate.
      if (this.overPlaceLimit) {
        this.confirmError = this.placeLimitMessage;
        return;
      }

      // This step does not persist anything. Saving custom/CSV places is a
      // separate, explicit action in the Review step ("Save" in the Places to
      // Save section). Here we simply return the selected places back to the
      // caller of the modal.
      const { resolve } = this.placeSelectionState;
      this.confirmError = '';

      this.isFinalizing = true;

      if (typeof resolve === 'function') {
        // Translate every selected place into a GeoJSON Feature. When a place
        // has no explicit geometry, derive one — a place that is just a point
        // (carrying centerLngLat, or a CSV previewFeatures Point) is wrapped as
        // a Point geometry via deriveGeometry. Only places with no resolvable
        // location at all are dropped.
        const features = this.selectedPlaces
          .map((p) => {
            const geometry = p.geometry || deriveGeometry(p);
            if (!geometry) return null;
            return {
              type: 'Feature',
              properties: {
                name: p.name,
                near_poi_id: p.near_poi_id,
                placematrix_id: p.placematrix_id,
                unique_name: p.unique_name,
                vp_id: p.vp_id,
                centerLngLat: p.centerLngLat || null,
              },
              geometry,
            };
          })
          .filter((feature) => feature !== null);

        const mergedGeojson = {
          type: 'FeatureCollection',
          features,
        };

        resolve({
          selected_places: this.selectedPlaces,
          merged_geojson: mergedGeojson,
          selectedLocationMethod: 'geojson',
          polygonInputOptions: {
            polygonFormat: 'GEOJSON',
            geojson: mergedGeojson,
          },
        });
      }

      this.$sessionStore.commit('closePlaceSelectionModal');
    },
    cancelSelection() {
      if (this.isFinalizing) return;
      if (!this.confirmDiscardActiveEdit()) return;

      if (this.reviewEditingPlaceId) {
        this.pendingReviewEditPayload = null;
      }

      const { reject } = this.placeSelectionState;
      this.isFinalizing = true;
      if (typeof reject === 'function') {
        reject({ cancelled: true });
      }
      this.$sessionStore.commit('closePlaceSelectionModal');
    },
  },
  beforeDestroy() {
    // Teardown: clear any pending CSV job poll timer (Requirement 3.4) and the
    // "places added" counter-pulse timer.
    this.cancelCsvJobPolling();
    this.clearCountPulseTimer();
    this.clearAddedConfirmTimer();
    this.clearPlaceLimitNoticeTimer();
  },
};
</script>

<style lang="scss" scoped>
#place-selection-modal___BV_modal_outer_ {
  z-index: 2147482999 !important;
}

.modal-body-inner {
  padding: 0;
}

.place-selection-two-column {
  height: 100%;
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 480px;  
}

.place-selection-right {
  width: 100%;
  min-width: 360px;
  border-right: 1px solid #e2e8f0;
  overflow: hidden;
  // Grid items default to min-height: auto, which lets this column grow past
  // its track height instead of constraining its children. Pin it to 0 so a
  // full-height child (the Review step) can resolve height: 100% and scroll
  // internally rather than overflowing under `overflow: hidden` (clipping rows
  // with no scrollbar).
  min-height: 0;
}

.place-selection-left {
  width: 100%;
}

// The Review step is a direct child of .place-selection-right (the tabs branch
// gets its own full-height wiring below). Give it the same full-height +
// min-height: 0 treatment so PlaceSelectionReview's internal `overflow-y: auto`
// engages when the selected-places list is long.
::v-deep .place-selection-right > .place-selection-review {
  height: 100%;
  min-height: 0;
}

::v-deep .place-selection-right > .tabs {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

::v-deep .place-selection-right .tab-content {
  flex: 1;
  min-height: 0;
  height: 100%;
}

::v-deep .place-selection-right .tab-content > .tab-pane.active {
  height: 100%;
  min-height: 0;
}

.modal-footer-bar {
  background: #fff;
}

::v-deep .modal-header {
  border-bottom: 1px solid #e2e8f0;
  padding: 0.75rem 1rem !important;
  align-items: center;
}

::v-deep .modal-body {
  padding: 0 !important;
}

::v-deep .nav-tabs {
  border-bottom: 1px solid #e2e8f0;
}

::v-deep .tab-content {
  padding: 0;
}

/* "Places added" feedback: counter pulse on the header button. */
.selected-places-btn {
  transition: transform 0.15s ease;
}

.selected-places-btn.count-pulse {
  animation: selected-places-pulse 0.6s ease;
}

@keyframes selected-places-pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(79, 20, 220, 0.5);
  }
  30% {
    transform: scale(1.12);
    box-shadow: 0 0 0 6px rgba(79, 20, 220, 0);
  }
  60% {
    transform: scale(0.98);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(79, 20, 220, 0);
  }
}

/* Transient "+N" badge that flies toward the header counter button. It starts
   near the top of the right-hand panel and animates toward the button's live
   position (via the --flight-end-* CSS vars set in JS), falling back to a
   fixed top-right anchor. */
.added-flight {
  position: fixed;
  left: var(--flight-start-x, 70%);
  top: var(--flight-start-y, 120px);
  z-index: 2147483000;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  background: #4f14dc;
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.2;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(18, 38, 63, 0.25);
  animation: added-flight-move 0.7s ease-in forwards;
}

@keyframes added-flight-move {
  0% {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.8);
  }
  15% {
    opacity: 1;
    transform: translate(-50%, -4px) scale(1.05);
  }
  100% {
    opacity: 0;
    left: var(--flight-end-x, 92%);
    top: var(--flight-end-y, 24px);
    transform: translate(-50%, 0) scale(0.6);
  }
}

/* Inline "N places added" footer confirmation, shown next to the selected
   count and faded in/out via the added-confirm-fade transition. */
.added-confirm {
  display: inline-flex;
  align-items: center;
  color: #00864e;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

.added-confirm-fade-enter-active,
.added-confirm-fade-leave-active {
  transition: opacity 0.3s ease;
}

.added-confirm-fade-enter,
.added-confirm-fade-leave-to {
  opacity: 0;
}

/* Accessibility: honor reduced-motion by neutralizing the animations. The JS
   also skips triggering them, but this is a defensive belt-and-suspenders. */
@media (prefers-reduced-motion: reduce) {
  .selected-places-btn.count-pulse {
    animation: none;
  }
  .added-flight {
    animation: none;
    opacity: 0;
  }
  .added-confirm-fade-enter-active,
  .added-confirm-fade-leave-active {
    transition: none;
  }
}
</style>
<style>

  .modal-xxl {
    max-width: unset !important;
    margin: 0 0 0 80px !important;  
  }
  .modal-xxl .modal-content {
      min-height: 100vh;
  }
  .modal-xxl .modal-body-inner {
      height: calc(100vh - 120px);
  }
  .modal-xxl .modal-footer-bar {
      position: sticky;
      bottom: 0;
      z-index: 10;
  }

</style>